import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { getAdminProfile, updateAdminProfile } from '../services/admin_profile';
import { USER_PROFILE } from '../services/auth';

const AdminProfile = () => {
  const [formData, setFormData] = useState({
    code: '',
    name: '',
    email: '',
    phone: '',
    current_password: '',
    new_password: '',
    confirm_password: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    setLoading(true);
    const [err, data] = await getAdminProfile();
    if (!err && data) {
      setFormData({
        code: data?.code || '',
        name: data?.name || '',
        email: data?.email || '',
        phone: data?.phone || '',
        current_password: '',
        new_password: '',
        confirm_password: '',
      });
    }
    setLoading(false);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) nextErrors.name = ['NAME IS REQUIRED'];
    if (!formData.email.trim()) nextErrors.email = ['EMAIL IS REQUIRED'];
    if (!formData.phone.trim()) nextErrors.phone = ['PHONE IS REQUIRED'];
    if (formData.phone && !/^\d{10}$/.test(formData.phone)) {
      nextErrors.phone = ['INVALID PHONE NUMBER'];
    }
    if (formData.current_password || formData.new_password || formData.confirm_password) {
      if (!formData.current_password) nextErrors.current_password = ['CURRENT PASSWORD IS REQUIRED'];
      if (!formData.new_password) nextErrors.new_password = ['NEW PASSWORD IS REQUIRED'];
      if (formData.new_password && formData.new_password.length < 6) {
        nextErrors.new_password = ['NEW PASSWORD MUST BE AT LEAST 6 CHARACTERS'];
      }
      if (formData.new_password !== formData.confirm_password) {
        nextErrors.confirm_password = ['PASSWORDS DO NOT MATCH'];
      }
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;
    if (name === 'phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    } else if (['name', 'email'].includes(name)) {
      value = value.toUpperCase();
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    const payload = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
    };
    if (formData.current_password && formData.new_password) {
      payload.current_password = formData.current_password;
      payload.new_password = formData.new_password;
    }
    const [err, data] = await updateAdminProfile(payload);
    if (!err) {
      setErrors({});
      setFormData((prev) => ({
        ...prev,
        name: data?.name || prev.name,
        email: data?.email || prev.email,
        phone: data?.phone || prev.phone,
        current_password: '',
        new_password: '',
        confirm_password: '',
      }));
      const existingProfile = JSON.parse(localStorage.getItem(USER_PROFILE) || '{}');
      localStorage.setItem(
        USER_PROFILE,
        JSON.stringify({
          ...existingProfile,
          name: data?.name || formData.name,
          email: data?.email || formData.email,
          phone: data?.phone || formData.phone,
          code: data?.code || formData.code,
        })
      );
      toast.success('PROFILE UPDATED SUCCESSFULLY.');
    } else {
      setErrors(err || {});
    }
    setLoading(false);
  };

  return (
    <div className="p-4 w-full uppercase">
      <h2 className="text-2xl font-bold mb-4 text-[#293686]">ADMIN PROFILE</h2>
      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded shadow">
        <div>
          <label className="block text-sm font-medium">CODE</label>
          <input
            type="text"
            className="w-full mt-1 px-3 py-2 border rounded bg-gray-100 text-gray-600"
            value={formData.code}
            readOnly
            disabled
          />
        </div>

        <div>
          <label className="block text-sm font-medium">NAME</label>
          <input
            name="name"
            type="text"
            className="w-full mt-1 px-3 py-2 border rounded"
            value={formData.name}
            onChange={handleChange}
          />
          {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name[0]}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">EMAIL</label>
          <input
            name="email"
            type="email"
            className="w-full mt-1 px-3 py-2 border rounded"
            value={formData.email}
            onChange={handleChange}
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email[0]}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">PHONE</label>
          <input
            name="phone"
            type="tel"
            className="w-full mt-1 px-3 py-2 border rounded"
            value={formData.phone}
            onChange={handleChange}
          />
          {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone[0]}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">CURRENT PASSWORD</label>
          <div className="relative">
            <input
              name="current_password"
              type={showPasswords.current ? 'text' : 'password'}
              className="w-full mt-1 px-3 py-2 border rounded pr-10"
              value={formData.current_password}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('current')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPasswords.current ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
            </button>
          </div>
          {errors.current_password && <p className="text-red-500 text-sm mt-1">{errors.current_password[0]}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">NEW PASSWORD</label>
          <div className="relative">
            <input
              name="new_password"
              type={showPasswords.new ? 'text' : 'password'}
              className="w-full mt-1 px-3 py-2 border rounded pr-10"
              value={formData.new_password}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('new')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPasswords.new ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
            </button>
          </div>
          {errors.new_password && <p className="text-red-500 text-sm mt-1">{errors.new_password[0]}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium">CONFIRM NEW PASSWORD</label>
          <div className="relative">
            <input
              name="confirm_password"
              type={showPasswords.confirm ? 'text' : 'password'}
              className="w-full mt-1 px-3 py-2 border rounded pr-10"
              value={formData.confirm_password}
              onChange={handleChange}
            />
            <button
              type="button"
              onClick={() => togglePasswordVisibility('confirm')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2"
            >
              {showPasswords.confirm ? <EyeSlashIcon className="h-5 w-5 text-gray-500" /> : <EyeIcon className="h-5 w-5 text-gray-500" />}
            </button>
          </div>
          {errors.confirm_password && <p className="text-red-500 text-sm mt-1">{errors.confirm_password[0]}</p>}
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? 'SAVING...' : 'SAVE CHANGES'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminProfile;
